import { KnowledgeCheckComponentContent } from '../../../types/elearning';
import { useState } from 'react';
import { CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/outline';
import Button from '../../ui/Button';

interface KnowledgeCheckViewerProps {
  content: KnowledgeCheckComponentContent;
}

function shortAnswerMatches(userAnswer: unknown, answerKey: string | undefined): boolean {
  const answer = String(userAnswer ?? '').trim().toLowerCase();
  if (!answer || !answerKey) return false;
  const keys = answerKey
    .split(',')
    .map((key) => key.trim().toLowerCase())
    .filter(Boolean);
  return keys.some((key) => answer.includes(key));
}

export default function KnowledgeCheckViewer({ content }: KnowledgeCheckViewerProps) {
  const { question, type, options, correctAnswer, answerKey, explanation, points } = content;
  const [selectedAnswer, setSelectedAnswer] = useState<any>(null);
  const [submitted, setSubmitted] = useState(false);
  const [isCorrect, setIsCorrect] = useState<boolean | null>(null);
  const hasAnswer =
    type === 'short-answer'
      ? String(selectedAnswer ?? '').trim().length > 0
      : selectedAnswer !== null && selectedAnswer !== undefined;

  const handleSubmit = () => {
    if (!hasAnswer) {
      return;
    }

    let correct = false;

    if (type === 'multiple-choice') {
      const selectedOption = options?.find((_, index) => index === selectedAnswer);
      correct = selectedOption?.isCorrect || false;
    } else if (type === 'true-false') {
      correct = selectedAnswer === correctAnswer;
    } else if (type === 'short-answer') {
      correct = shortAnswerMatches(selectedAnswer, answerKey);
    }

    setIsCorrect(correct);
    setSubmitted(true);
  };

  const handleReset = () => {
    setSelectedAnswer(null);
    setSubmitted(false);
    setIsCorrect(null);
  };

  return (
    <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-4">
      <div className="flex items-start gap-2 mb-4">
        <div className="flex-shrink-0 w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-semibold">
          ?
        </div>
        <div className="flex-1">
          <h4 className="font-semibold text-gray-900 mb-2">Knowledge Check</h4>
          <p className="text-gray-700 mb-4">{question}</p>
        </div>
      </div>

      {!submitted ? (
        <>
          {type === 'multiple-choice' && options && (
            <div className="space-y-2 mb-4">
              {options.map((option, index) => (
                <button
                  key={index}
                  onClick={() => setSelectedAnswer(index)}
                  className={`w-full text-left px-4 py-3 rounded-lg border-2 transition-colors ${
                    selectedAnswer === index
                      ? 'border-blue-600 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  {option.text}
                </button>
              ))}
            </div>
          )}

          {type === 'true-false' && (
            <div className="grid grid-cols-2 gap-3 mb-4">
              <button
                onClick={() => setSelectedAnswer(true)}
                className={`px-4 py-3 rounded-lg border-2 transition-colors ${
                  selectedAnswer === true
                    ? 'border-blue-600 bg-blue-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                True
              </button>
              <button
                onClick={() => setSelectedAnswer(false)}
                className={`px-4 py-3 rounded-lg border-2 transition-colors ${
                  selectedAnswer === false
                    ? 'border-blue-600 bg-blue-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                False
              </button>
            </div>
          )}

          {type === 'short-answer' && (
            <textarea
              value={selectedAnswer || ''}
              onChange={(e) => setSelectedAnswer(e.target.value)}
              placeholder="Type your answer here..."
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:border-blue-600 focus:ring-2 focus:ring-blue-600 focus:ring-offset-0 mb-4"
              rows={3}
            />
          )}

          <Button
            variant="primary"
            onClick={handleSubmit}
            disabled={!hasAnswer}
          >
            Check Answer
          </Button>
        </>
      ) : (
        <div className="space-y-4">
          <div
            className={`flex items-center gap-2 p-4 rounded-lg ${
              isCorrect ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
            }`}
          >
            {isCorrect ? (
              <CheckCircleIcon className="h-5 w-5" />
            ) : (
              <XCircleIcon className="h-5 w-5" />
            )}
            <span className="font-semibold">
              {isCorrect ? 'Correct!' : 'Incorrect'}
            </span>
            {typeof points === 'number' && (
              <span className="ml-auto text-sm">
                {isCorrect
                  ? `${points} practice ${points === 1 ? 'point' : 'points'}`
                  : '0 practice points'}
              </span>
            )}
          </div>

          {explanation && (
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <p className="text-sm font-semibold text-gray-700 mb-1">Explanation:</p>
              <p className="text-sm text-gray-600">{explanation}</p>
            </div>
          )}

          <Button variant="tertiary" onClick={handleReset}>
            Try Again
          </Button>
        </div>
      )}
    </div>
  );
}
